import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Button,
  CardMedia,
  Stack,
  Chip,
} from "@mui/material";
import { getPublicWebsiteBootstrap, getPublicWebsiteBootstrapByHost } from "../../api/client";

const tokenStyleVars = (tokens = {}) => ({
  "--site-primary": tokens.primaryColor || "#0F4C81",
  "--site-secondary": tokens.secondaryColor || "#F5A623",
  "--site-accent": tokens.accentColor || "#2ECC71",
  "--site-text": tokens.textColor || "#1F2937",
  "--site-bg": tokens.backgroundColor || "#FFFFFF",
  "--site-radius": tokens.borderRadius || "8px",
  "--site-font": tokens.fontFamily || "Inter, sans-serif",
});

const markingPlaceholderDataUri = (label = "Imagem") => {
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="600" viewBox="0 0 1200 600">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#f8fafc"/>
      <stop offset="1" stop-color="#eef2ff"/>
    </linearGradient>
  </defs>
  <rect x="24" y="24" width="1152" height="552" rx="28" fill="url(#g)" stroke="#cbd5e1" stroke-width="6" stroke-dasharray="18 14"/>
  <text x="600" y="306" text-anchor="middle" font-family="Arial, sans-serif" font-size="56" font-weight="800" fill="#94a3b8">${String(
    label
  )}</text>
  <text x="600" y="380" text-anchor="middle" font-family="Arial, sans-serif" font-size="26" font-weight="700" fill="#94a3b8">marcação</text>
</svg>`.trim();

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export default function PublicWebsitePage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const company = searchParams.get("company") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = slug
          ? await getPublicWebsiteBootstrap(slug, company)
          : await getPublicWebsiteBootstrapByHost(company);
        if (!mounted) return;
        setData(res || null);
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Não foi possível carregar o website.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [slug, company]);

  const site = data?.website || {};
  const mainImages = site.mainImages || {};
  const products = Array.isArray(data?.featuredProducts) ? data.featuredProducts : [];
  const banners = Array.isArray(data?.banners) ? data.banners : [];
  const styleVars = useMemo(() => tokenStyleVars(site.themeTokens || {}), [site.themeTokens]);
  const heroBanner = banners[0] || null;
  const secondaryBanners = banners.slice(1, 4);
  const brandName = site.channelName || "Website Viação";
  const menuItems = Array.isArray(site.menuItems) ? site.menuItems : [];

  const heroImageSrc = mainImages?.heroImageSrc || null;
  const heroBgPlaceholder = markingPlaceholderDataUri("Hero");
  const heroBgImage = heroImageSrc
    ? heroImageSrc
    : heroBgPlaceholder;

  if (loading) {
    return (
      <Box sx={{ minHeight: "80vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        ...styleVars,
        minHeight: "100vh",
        backgroundColor: "var(--site-bg)",
        color: "var(--site-text)",
        fontFamily: "var(--site-font)",
      }}
    >
      <Box sx={{ borderBottom: "1px solid #e5e7eb", py: 2 }}>
        <Container sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" sx={{ color: "var(--site-primary)", fontWeight: 700 }}>
            {brandName}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {menuItems.map((item) => (
              <Button key={item} size="small" sx={{ color: "var(--site-primary)" }}>
                {String(item).toUpperCase()}
              </Button>
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 4, md: 7 },
          background:
            `linear-gradient(90deg, rgba(15,76,129,0.85), rgba(15,76,129,0.45)), url("${heroBgImage}") center/cover no-repeat`,
          color: "#fff",
        }}
      >
        <Container>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1, mb: 1.5 }}>
                {heroBanner?.title || `Viaje com ${brandName}`}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.92, mb: 2 }}>
                {heroBanner?.subtitle ||
                  "Compre passagens, acompanhe novidades e fale com a viação em um website pronto para uso."}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "var(--site-accent)",
                    color: "#fff",
                    borderRadius: "var(--site-radius)",
                    "&:hover": { bgcolor: "var(--site-accent)" },
                  }}
                >
                  Comprar Passagem
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(255,255,255,0.7)",
                    color: "#fff",
                    borderRadius: "var(--site-radius)",
                  }}
                >
                  Ver Horários
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: "var(--site-radius)", bgcolor: "rgba(255,255,255,0.98)" }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Website online
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--site-primary)", wordBreak: "break-all" }}>
                    {site.tempUrl}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {["Compra rápida", "Suporte imediato", "Visual personalizável"].map((label) => (
            <Grid item xs={12} md={4} key={label}>
              <Card sx={{ borderRadius: "var(--site-radius)", border: "1px solid #e5e7eb" }}>
                <CardContent>
                  <Chip
                    size="small"
                    label={label}
                    sx={{ mb: 1, bgcolor: "rgba(15,76,129,0.08)", color: "var(--site-primary)" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Estrutura pronta para vendas e atendimento, com conteúdo integrado do workspace.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {secondaryBanners.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {secondaryBanners.map((banner, index) => {
              const configuredSrc = mainImages?.secondaryBannerImageSrcs?.[index] || null;
              const img = configuredSrc || markingPlaceholderDataUri(`Banner ${index + 1}`);
              return (
                <Grid item xs={12} md={4} key={banner.id}>
                  <Card sx={{ borderRadius: "var(--site-radius)", overflow: "hidden", height: "100%" }}>
                    <CardMedia component="img" height="150" image={img} alt={banner.imageAlt || "Banner"} />
                    <CardContent>
                      <Typography variant="h6">{banner.title || "Banner"}</Typography>
                      <Typography variant="body2">{banner.subtitle || ""}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Typography variant="h5" sx={{ mb: 2, color: "var(--site-primary)" }}>
          Produtos em destaque
        </Typography>
        {products.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            Nenhum produto em destaque ainda. Cadastre produtos no workspace para aparecerem aqui.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={3} key={product.id}>
                <Card sx={{ borderRadius: "var(--site-radius)", height: "100%", overflow: "hidden" }}>
                  {product.images?.[0] ? (
                    <CardMedia
                      component="img"
                      height="150"
                      image={product.images[0]}
                      alt={product.name || "Produto"}
                    />
                  ) : null}
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {product.sku || "-"}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1, color: "var(--site-accent)" }}>
                      R$ {Number(product.price || 0).toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Box sx={{ mt: 4, py: 3, borderTop: "1px solid #e5e7eb", bgcolor: "rgba(15,76,129,0.04)" }}>
        <Container sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            {brandName} - website gerado automaticamente pela plataforma.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Powered by Xpro
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
